/**
 * Hub Usage API client (T04).
 *
 * Wraps the two read-only endpoints exposed by the hub's UsageController
 * (T09, services/hub/ src/modules/usage/usage.controller.ts):
 *   GET /api/usage          — paginated per-request log
 *   GET /api/usage/summary  — KPI rollups + welcome bonus totals
 *
 * Field shape mirrors the router's UsageRow (src/services/usage-service.ts).
 * Money values (`cost_usd`) arrive as numbers (router rounds to 6dp in SQL);
 * format with up to 8 decimal places via the view's `formatUsd8`.
 *
 * Date params (`from`, `to`) are forwarded verbatim to the hub controller's
 * Zod DTO which expects full ISO datetime strings (YYYY-MM-DDTHH:mm:ss.fffZ).
 * The store normalises YYYY-MM-DD from the URL into this range format.
 */
import { request } from './client';

/** Matches router/src/services/usage-service.ts UsageRow exactly. */
export interface UsageRow {
  timestamp: string;
  request_id: string | null;
  internal_req_id: string | null;
  provider: string;
  model: string;
  api_key_id: string | null;
  status: string;
  http_status: number | null;
  prompt_tokens: number | null;
  completion_tokens: number | null;
  total_tokens: number | null;
  latency_ms: number;
  cost_usd: number;
  cost_native: number | null;
  cost_native_unit: string | null;
  error_code: string | null;
}

export interface UsageListResponse {
  rows: UsageRow[];
  total: number;
}

export interface UsageDailyBucket {
  date: string;
  tokens: number;
  requests: number;
  cost_usd: number;
}

export interface UsageRateEntry {
  model: string;
  value_usd: number;
}

export interface UsageSummaryResponse {
  total_spent_usd: number;
  total_tokens_in: number;
  total_tokens_out: number;
  total_tokens: number;
  total_requests: number;
  last_30d_daily: UsageDailyBucket[];
  rate_per_1m: UsageRateEntry[];
  /** Decimal string (PostgreSQL NUMERIC(20,8)). */
  welcome_bonus_total: string;
  welcome_bonus_used: string;
}

export interface ListUsageParams {
  /** ISO datetime lower bound (inclusive). */
  from?: string;
  /** ISO datetime upper bound (exclusive). */
  to?: string;
  /** When true, return only rows with status = 'error'. */
  errors_only?: boolean;
  /** 1-indexed page number. */
  page?: number;
  /** Rows per page, 1–200. */
  page_size?: number;
}

export const usageApi = {
  /**
   * Paginated per-request log.
   *
   * Params are forwarded to the hub controller which validates them via Zod.
   * Omit keys that are undefined/null so the server applies its defaults.
   */
  list: (params: ListUsageParams = {}): Promise<UsageListResponse> => {
    const q = new URLSearchParams();
    if (params.from != null) q.set('from', params.from);
    if (params.to != null) q.set('to', params.to);
    if (params.errors_only != null) q.set('errors_only', String(params.errors_only));
    if (params.page != null) q.set('page', String(params.page));
    if (params.page_size != null) q.set('page_size', String(params.page_size));
    const qs = q.toString();
    return request<UsageListResponse>(`/api/usage${qs ? `?${qs}` : ''}`);
  },

  /**
   * KPI summary + welcome bonus totals.
   * The `since` param filters totals; the 30-day daily series is always the
   * last 30 days regardless of `since`.
   */
  summary: (params: { since?: string } = {}): Promise<UsageSummaryResponse> => {
    const q = new URLSearchParams();
    if (params.since != null) q.set('since', params.since);
    const qs = q.toString();
    return request<UsageSummaryResponse>(`/api/usage/summary${qs ? `?${qs}` : ''}`);
  },
};